import GenericListModule from "../../../gd-modules/generic-list/generic-list-module"
import FieldsCollection from "../../../fields3/collection/base/fields-collection"
import FieldConfig from "../../../fields3/proto/field-config"
import {FieldType} from "../../../fields3/field-type"
import {GlistConfig} from "../../../gd-modules/generic-list/types/glist-config"
import {GlistColumnAlignment, GlistColumnType} from "../../../gd-modules/generic-list/manager/columns-manager/column"
import {GlistWidthMode} from "../../../gd-modules/generic-list/manager/columns-manager/const"
import {GlistHeaderMode} from "../../../gd-modules/generic-list/const"
import {SortDirection} from "../../../data-structures/sort"
import EntitySelectorItem from "./item"
import EntitySelectorGlistTitleCell from "./title-cell"
import EntitySelectorGlistDateCell from "./date-cell"

// Sized for the widest date setting, "MMM D, YYYY" ("May 30, 2026", ~83px at
// 14px) + 2x10px cell padding. "LAST OPENED" (~75px) fits only because
// selector.scss drops the header label's 30px sort-icon reservation.
const DATE_COLUMN_WIDTH = 110

// The row inset, as the glist's OWN padding rather than the container's: it is
// rendered as a spacer column inside each row, so the header band still runs the
// full width of the panel while the cells line up under the search box above.
// Matches `$es-inset` in selector.scss.
const ROW_INSET = 20

// The only thing a tab differs in: its two column headers and what a click does.
export type EntitySelectorGlistOptions = {
    titleLabel          : string    // "TASK" / "TITLE" -- the skin uppercases it
    dateLabel           : string    // "LAST OPENED" / "DATE"
    noItemsMessage      : string

    // DESC everywhere ("recent first") except meeting Upcoming, which reads
    // more naturally soonest-first.
    dateSortDirection?  : SortDirection

    onSelect            : {(item: EntitySelectorItem): void}
}

// The list every picker tab renders: name + project chip on the left, a date on
// the right. Task/Recent, Document and Meeting all build THIS module -- they
// differ in where the rows come from, not in how a row looks.
export default class EntitySelectorGlistModule extends GenericListModule {

    public options: EntitySelectorGlistOptions

    constructor(options: EntitySelectorGlistOptions) {

        // Two ad-hoc fields for the two columns -- neither is ever RENDERED by
        // fields3 (the title has renderTitleComponent and the date its own
        // cell), but their sortValue is: title as STRING, date as MOMENT so it
        // sorts on the instant rather than doing a string compare of a Moment
        // object (`EntitySelectorItem.date` is already parsed, so MOMENT's own
        // parsing is a no-op here).
        const fieldsConfig: FieldConfig[] = [
            {fieldId: 'title',  label: options.titleLabel,  type: FieldType.STRING},
            {fieldId: 'date',   label: options.dateLabel,   type: FieldType.MOMENT},
        ]

        const fieldsCollection = new FieldsCollection(fieldsConfig)

        const config: GlistConfig = {
            skin: {
                // `on-gray` is the skin's own variant for a list whose header is
                // a grey band rather than a hover-revealed strip -- it paints
                // `.master-header.sticky` and keeps the rows white. It is what
                // makes the column titles read as a header, so it goes together
                // with headerMode STICKY below.
                className           : 'ui5-glist skin-modern on-gray gl-h-40',
                defaultAlignment    : GlistColumnAlignment.LEFT,
                paddingLeft         : ROW_INSET,
                paddingRight        : ROW_INSET,
            },

            fields  : fieldsCollection,
            columns : [
                {
                    // NOT GlistColumnType.TITLE: that type routes the HEADER cell
                    // through `GlistHeaderCellTitle` instead of the `ListHeaderTile`
                    // every other column uses -- a `cell-title` box with a 15px
                    // `ui5-title-small` heading, not the 11px uppercase grey label
                    // the rest of the header reads in. `renderTitleComponent`
                    // below is keyed off `titleField`, not this type, so the ROW
                    // cell is unaffected either way -- only the header was wrong.
                    fieldId : 'title',
                    label   : options.titleLabel
                },
                {
                    type        : GlistColumnType.FIELD,
                    fieldId     : 'date',
                    label       : options.dateLabel,
                    width       : DATE_COLUMN_WIDTH,
                    component   : EntitySelectorGlistDateCell
                },
            ],

            // AUTO: the same list has to fill a full-width modal and a 550px
            // mention popover, so the title takes whatever is left over.
            widthMode               : GlistWidthMode.AUTO,

            titleField              : 'title',
            renderTitleComponent    : EntitySelectorGlistTitleCell,

            // No sort button is rendered (`canSort` stays unset/false below), so
            // this is a fixed order rather than a user-changeable one: the date
            // column primary, title as the tie-break when two rows land on the
            // same instant.
            sortBy                  : {fieldId: 'date',  direction: options.dateSortDirection ?? SortDirection.DESC},
            defaultSortBy           : {fieldId: 'title', direction: SortDirection.ASC},

            // STICKY, not DEFAULT: the grey band `on-gray` paints is on
            // `.master-header.sticky`, and a header the rows scroll under is
            // what the design shows anyway.
            headerMode              : GlistHeaderMode.STICKY,

            noItemsMessage          : options.noItemsMessage,
        }

        super(fieldsCollection, config)

        this.options = options
    }

    onItemClick(id: ID, item: EntitySelectorItem) {
        this.options.onSelect(item)
    }

    // `GenericListModule.getMaxWidth()` subtracts `gd.layout.scrollWidth`
    // (~15-17px on Windows) from the parent's clientWidth, reserving room for a
    // NATIVE scrollbar -- correct for a glist that scrolls itself, but this one
    // is wrapped in `<VScroll>`, which force-hides its native scrollbar
    // (`v-scroll-no-webkit-scroll`, always on) in favour of its own drag track,
    // which doesn't take layout space. Left unreserved, the list -- and the
    // header band with it, since `master-header.tsx` sets its width from the
    // SAME calculation -- rendered exactly `scrollWidth` px short of the panel.
    getMaxWidth() {
        if (this.skin.maxWidth) return this.skin.maxWidth
        if (this.parentWidth)   return this.parentWidth

        return gd.layout.widthMain
    }
}
